import { publicEnv } from '@/lib/config/public-env';
/**
 * @fileoverview Authentication guards and redirect handlers
 * Contains type guards and utility functions for handling authentication state
 * @module @auth/types/guards
 */

// === Next.js ===
import type { Session } from 'next-auth';
import { NextResponse } from 'next/server';

// === Types & Interfaces ===
import type { AuthActionResult, AuthenticationResponse, UserProfile } from '@/@auth/types';
import type { TypeGuard } from '@/types/utils';
import type { SessionSource } from './utils';

// === Utils & Helpers ===
import { EVENTS } from '@/lib/features/tracking/events';
import { tracking } from '@/lib/features/tracking/tracking';
import type { AuthEventProps } from '@/lib/features/tracking/types';
import { getServerSession } from '../config/auth';
import AuthService from '../services';

// === Constants ===
import { AUTH_ERROR_TYPES } from './constants';
import { Routes } from '@/app/routes';
const PORTAL_URL = publicEnv('NEXT_PUBLIC_STUDIO_URL');
const AUTH_URL = process.env.NEXTAUTH_URL;

/**
 * Handles authentication failures by redirecting to sign-in
 * @param error - The error that occurred
 * @param redirectTo - Optional URL to redirect to after sign-in
 * @returns URL string for redirection
 */
export function handleAuthFailure(error: unknown, redirectTo?: string): string {
    tracking.errorWithSource(error as Error, 'handleAuthFailure', { redirectTo });
    const signInUrl = new URL(Routes.SIGNIN, AUTH_URL);
    if (redirectTo) {
        signInUrl.searchParams.set('callbackUrl', redirectTo);
    }
    return signInUrl.toString();
}

/**
 * Validates if a URL is safe for redirection
 * @param url - URL to validate
 * @returns boolean indicating if URL is safe
 */
function isValidRedirectUrl(url: string): boolean {
    try {
        const redirectUrl = new URL(url, PORTAL_URL);
        const hostname = redirectUrl.hostname;

        // Check if URL is relative or matches allowed domains
        return (
            url.startsWith('/') ||
            hostname === new URL(PORTAL_URL || '').hostname ||
            hostname === new URL(AUTH_URL || '').hostname
        );
    } catch {
        return false;
    }
}

/**
 * Validates a client-side NextAuth session
 * @param session - The client session to validate
 * @returns boolean indicating if session is valid
 */
async function validateClientSession(session: Session): Promise<boolean> {
    // Check for session errors
    if (session.error) {
        tracking.event<AuthEventProps>(EVENTS.USER.SESSION_ERROR, {
            errorType: session.error,
            source: 'handleUnauthorizedRedirect.client',
        });
        return false;
    }

    // Check for required tokens in client session
    if (!session.accessToken || !session.idToken) {
        tracking.event<AuthEventProps>(EVENTS.AUTH.NO_ID_TOKEN_FOUND, {
            source: 'handleUnauthorizedRedirect.client',
        });
        return false;
    }

    return true;
}

/**
 * Validates server-side session (UserProfile)
 * @param profile - The user profile to validate
 * @returns boolean indicating if session is valid
 */
async function validateServerSession(profile: UserProfile): Promise<boolean> {
    // Verify essential user profile data
    if (!profile.id || !profile.email) {
        tracking.event<AuthEventProps>(EVENTS.USER.SESSION_ERROR, {
            errorType: AUTH_ERROR_TYPES.INVALID_PROFILE,
            source: 'handleUnauthorizedRedirect.server',
        });
        return false;
    }

    // Verify auth cookies exist
    try {
        const { idToken } = await AuthService.getAuthCookies();
        if (!idToken) {
            tracking.event<AuthEventProps>(EVENTS.AUTH.NO_ID_TOKEN_FOUND, {
                source: 'handleUnauthorizedRedirect.server',
            });
            return false;
        }
    } catch (error) {
        tracking.errorWithSource(
            error instanceof Error ? error : new Error('Failed to validate auth cookies'),
            'handleUnauthorizedRedirect.server',
            {},
        );
        return false;
    }

    return true;
}

/**
 * Validates the session based on its source (client or server)
 * @param sessionSource - Object containing session and its source
 * @returns true if session is valid, false otherwise
 */
async function isValidSession(sessionSource: SessionSource): Promise<boolean> {
    const { type, session } = sessionSource;

    // Handle null session
    if (!session) {
        tracking.event<AuthEventProps>(EVENTS.USER.SESSION_ERROR, {
            errorType: AUTH_ERROR_TYPES.NO_SESSION,
            source: `handleUnauthorizedRedirect.${type}`,
        });
        return false;
    }

    // Route to appropriate validator based on session type
    if (type === 'client') {
        return validateClientSession(session as Session);
    }

    if (type === 'server') {
        return validateServerSession(session as UserProfile);
    }

    return false;
}

/**
 * Handles unauthorized errors by redirecting to appropriate auth pages
 * Works with both client-side (useSession) and server-side (getServerSession) sessions
 *
 * @param error - The error that triggered the redirect
 * @param clientSession - Optional client-side session from useSession
 * @returns NextResponse with redirect or null if no redirect needed
 */
export async function handleUnauthorizedRedirect(
    error: unknown,
    clientSession?: Session | null,
): Promise<NextResponse | null> {
    // Determine session source and validate
    const sessionSource: SessionSource = clientSession
        ? { type: 'client', session: clientSession }
        : { type: 'server', session: await getServerSession() };

    const isSessionValid = await isValidSession(sessionSource);

    if (!isSessionValid) {
        const signInUrl = new URL(Routes.SIGNIN, PORTAL_URL);
        signInUrl.searchParams.set('error', AUTH_ERROR_TYPES.SESSION_INVALID);
        if (sessionSource.type === 'client') {
            const sessionError = (sessionSource.session as Session)?.error;
            if (sessionError) {
                signInUrl.searchParams.set('errorType', sessionError);
            }
        }
        return NextResponse.redirect(signInUrl);
    }

    // Handle string errors (legacy support)
    if (typeof error === 'string' && error.startsWith('Unauthorized: ')) {
        const redirectUrl = error.split('Unauthorized: ')[1];
        if (!isValidRedirectUrl(redirectUrl)) {
            tracking.errorWithSource(
                new Error('Invalid redirect URL'),
                'handleUnauthorizedRedirect',
                { redirectUrl },
            );
            return NextResponse.redirect(new URL(Routes.SIGNIN, PORTAL_URL));
        }
        return NextResponse.redirect(new URL(redirectUrl, PORTAL_URL));
    }

    // Handle Error instances
    if (error instanceof Error) {
        // Track the unauthorized error
        tracking.event<AuthEventProps>(EVENTS.USER.SESSION_ERROR, {
            errorMessage: error.message,
            errorType: error.name,
            source: `handleUnauthorizedRedirect.${sessionSource.type}`,
        });

        // Handle specific error cases
        if (error.message === 'No session found' || error.message === 'Session expired') {
            return NextResponse.redirect(new URL(Routes.SIGNIN, PORTAL_URL));
        }

        if (error.message.startsWith('Unauthorized: ')) {
            const redirectUrl = error.message.split('Unauthorized: ')[1];
            if (!isValidRedirectUrl(redirectUrl)) {
                tracking.errorWithSource(
                    new Error('Invalid redirect URL'),
                    'handleUnauthorizedRedirect',
                    { redirectUrl },
                );
                return NextResponse.redirect(new URL(Routes.SIGNIN, PORTAL_URL));
            }
            return NextResponse.redirect(new URL(redirectUrl, PORTAL_URL));
        }

        // Handle token-related errors
        if (
            error.message === 'RefreshAccessTokenError' ||
            error.message === 'TokenExpiredError' ||
            error.message === 'No refresh token found'
        ) {
            const signInUrl = new URL(Routes.SIGNIN, PORTAL_URL);
            signInUrl.searchParams.set('error', 'SessionExpired');
            return NextResponse.redirect(signInUrl);
        }
    }

    // Log unhandled cases
    tracking.errorWithSource(
        error instanceof Error ? error : new Error('Unknown unauthorized error'),
        'handleUnauthorizedRedirect',
        {
            errorType: AUTH_ERROR_TYPES.UNHANDLED_CASE,
            sessionType: sessionSource.type,
        },
    );

    // Default fallback to sign-in page
    return NextResponse.redirect(new URL(Routes.SIGNIN, PORTAL_URL));
}

// === Type Guards ===

/**
 * Type guard to check if a value is a UserProfile
 * @param value - The value to check
 * @returns True if the value is a valid UserProfile
 */
export const isUserProfile: TypeGuard<UserProfile> = (value: unknown): value is UserProfile => {
    if (!value || typeof value !== 'object') return false;

    const profile = value as Partial<UserProfile>;

    return (
        typeof profile.id === 'string' &&
        typeof profile.email === 'string' &&
        Array.isArray(profile.groups) &&
        typeof profile.role === 'string' &&
        typeof profile.isAdmin === 'boolean' &&
        typeof profile.isInternal === 'boolean' &&
        // Optional fields
        (profile.name === undefined || typeof profile.name === 'string') &&
        (profile.familyName === undefined || typeof profile.familyName === 'string') &&
        (profile.givenName === undefined || typeof profile.givenName === 'string') &&
        (profile.salesforceOrgId === undefined || typeof profile.salesforceOrgId === 'string') &&
        (profile.salesforceOrg === undefined || typeof profile.salesforceOrg === 'string')
    );
};

/**
 * Type guard to check if a value is an AuthenticationResponse
 * @param value - The value to check
 * @returns True if the value is a valid AuthenticationResponse
 */
export const isAuthenticationResponse: TypeGuard<AuthenticationResponse> = (
    value: unknown,
): value is AuthenticationResponse => {
    if (!value || typeof value !== 'object') return false;

    const response = value as Partial<AuthenticationResponse>;

    return (
        typeof response.AccessToken === 'string' &&
        typeof response.RefreshToken === 'string' &&
        typeof response.IdToken === 'string' &&
        typeof response.TokenType === 'string' &&
        typeof response.ExpiresIn === 'number' &&
        isUserProfile(response.user)
    );
};

/**
 * Type guard to check if a value is an AuthActionResult
 * @param value - The value to check
 * @param dataGuard - Optional type guard for checking the data field
 * @returns True if the value is a valid AuthActionResult
 */
export const isAuthActionResult = <T>(
    value: unknown,
    dataGuard?: TypeGuard<T>,
): value is AuthActionResult<T> => {
    if (!value || typeof value !== 'object') return false;

    const response = value as Partial<AuthActionResult<T>>;

    return (
        typeof response.success === 'boolean' &&
        // Optional fields with type checking
        (response.data === undefined ||
            response.data === null ||
            (dataGuard ? dataGuard(response.data) : true)) &&
        (response.error === undefined || typeof response.error === 'string') &&
        (response.redirectUrl === undefined || typeof response.redirectUrl === 'string')
    );
};
